// Skins (§10): la personalitat visual de cada casa a la part pública. Una skin és un joc de tokens
// que es converteixen en variables CSS (--bt-*); els components no canvien mai. Sense zod: la part
// pública l'importa per aplicar els tokens (subruta `@blauturquesa/shared/skins`).
// Tipografies (§10): una llista tancada, perquè cada família es pugui carregar sola i amb pocs pesos.
export const FONTS = {
    georgia: { css: "Georgia, 'Times New Roman', serif", google: null },
    system: { css: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif", google: null },
    playfair: { css: "'Playfair Display', Georgia, serif", google: 'Playfair+Display:wght@400;600' },
    cormorant: { css: "'Cormorant Garamond', Georgia, serif", google: 'Cormorant+Garamond:wght@400;600' },
    fraunces: { css: "'Fraunces', Georgia, serif", google: 'Fraunces:wght@400;600' },
    dmSerif: { css: "'DM Serif Display', Georgia, serif", google: 'DM+Serif+Display' },
    inter: { css: "'Inter', system-ui, sans-serif", google: 'Inter:wght@400;600' },
    montserrat: { css: "'Montserrat', system-ui, sans-serif", google: 'Montserrat:wght@400;600' },
    josefin: { css: "'Josefin Sans', system-ui, sans-serif", google: 'Josefin+Sans:wght@400;600' },
    lora: { css: "'Lora', Georgia, serif", google: 'Lora:wght@400;600' },
};
export const FONT_IDS = Object.keys(FONTS);
export const SKIN_TOKEN_COLORS = [
    'bg', 'surface', 'text', 'textMuted', 'line', 'accent', 'onAccent', 'gradientFrom', 'gradientTo', 'onGradient',
];
/** El mínim de contrast que s'exigeix (WCAG AA per a text normal), §10. */
export const MIN_CONTRAST = 4.5;
const HEX = /^#[0-9a-f]{6}$/i;
export const isHexColor = (v) => HEX.test(v);
function luminance(hex) {
    const c = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}
/** Contrast entre dos colors (de 1 a 21), segons WCAG. */
export function contrast(a, b) {
    const [x, y] = [luminance(a), luminance(b)].sort((p, q) => q - p);
    return Math.round(((x + 0.05) / (y + 0.05)) * 100) / 100;
}
/** El color del punt mig entre dues parades del degradat. */
function midpoint(a, b) {
    const h = (s, i) => parseInt(s.slice(i, i + 2), 16);
    return `#${[1, 3, 5].map((i) => Math.round((h(a, i) + h(b, i)) / 2).toString(16).padStart(2, '0')).join('')}`;
}
/**
 * Comprovacions obligatòries (§10): l'editor no deixa desar una skin que no les passi, i el servidor
 * tampoc. El text sobre el degradat es comprova a les dues parades i al punt mig: un degradat de fosc
 * a clar no admet un sol color de text.
 */
export function validateSkin(t) {
    const problems = [];
    for (const f of SKIN_TOKEN_COLORS)
        if (!isHexColor(t[f]))
            problems.push({ key: 'invalidColor', field: f });
    for (const f of ['gradientAngle', 'radiusControl', 'radiusCard', 'radiusSheet']) {
        const max = f === 'gradientAngle' ? 360 : 40;
        if (!Number.isFinite(t[f]) || t[f] < 0 || t[f] > max)
            problems.push({ key: 'invalidNumber', field: f });
    }
    if (problems.length)
        return problems;
    const check = (key, a, b) => {
        const ratio = contrast(a, b);
        if (ratio < MIN_CONTRAST)
            problems.push({ key, ratio });
    };
    check('textOnBg', t.text, t.bg);
    check('textOnSurface', t.text, t.surface);
    check('mutedOnBg', t.textMuted, t.bg);
    check('mutedOnSurface', t.textMuted, t.surface);
    check('onAccent', t.onAccent, t.accent);
    check('onGradientFrom', t.onGradient, t.gradientFrom);
    check('onGradientMid', t.onGradient, midpoint(t.gradientFrom, t.gradientTo));
    check('onGradientTo', t.onGradient, t.gradientTo);
    return problems;
}
const rgba = (hex, a) => `rgb(${[1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)).join(' ')} / ${a})`;
/** Tokens → variables CSS (§10). Inclou els derivats (vidre, ombres) perquè el mode fosc quedi bé. */
export function skinCssVars(t) {
    const dark = t.mode === 'dark';
    return {
        '--bt-bg': t.bg,
        '--bt-surface': t.surface,
        '--bt-text': t.text,
        '--bt-text-muted': t.textMuted,
        '--bt-line': t.line,
        '--bt-accent': t.accent,
        '--bt-on-accent': t.onAccent,
        '--bt-gradient-from': t.gradientFrom,
        '--bt-gradient-to': t.gradientTo,
        '--bt-gradient-angle': `${t.gradientAngle}deg`,
        '--bt-on-gradient': t.onGradient,
        '--bt-font-display': FONTS[t.fontDisplay].css,
        '--bt-font-text': FONTS[t.fontText].css,
        '--bt-radius-control': `${t.radiusControl}px`,
        '--bt-radius-card': `${t.radiusCard}px`,
        '--bt-radius-sheet': `${t.radiusSheet}px`,
        '--bt-glass-bg': rgba(t.surface, dark ? 0.55 : 0.62),
        '--bt-glass-bg-strong': rgba(t.surface, dark ? 0.8 : 0.82),
        '--bt-glass-solid': t.surface,
        '--bt-glass-border': rgba(t.text, 0.08),
        '--bt-glass-highlight': rgba(dark ? t.text : '#ffffff', dark ? 0.08 : 0.7),
        'color-scheme': t.mode,
    };
}
/** URL de Google Fonts amb les famílies d'una skin (només les que no són del sistema), o null. */
export function skinFontsUrl(t) {
    const families = [...new Set([t.fontDisplay, t.fontText])].map((f) => FONTS[f].google).filter((g) => g !== null);
    return families.length ? `https://fonts.googleapis.com/css2?${families.map((f) => `family=${f}`).join('&')}&display=swap` : null;
}
// Joc inicial (§10): Base, Platja, Ciutat, Muntanya i Camp. Totes passen validateSkin (hi ha un test).
const shape = { radiusControl: 8, radiusCard: 12, radiusSheet: 16 };
export const BUILT_IN_SKINS = [
    { id: 'base', builtIn: true, name: 'Base', tokens: {
            mode: 'light', bg: '#fbfaf7', surface: '#ffffff', text: '#14201f', textMuted: '#4d5c5a', line: '#dfe3e1',
            accent: '#0b6e74', onAccent: '#ffffff', gradientFrom: '#ffd23f', gradientTo: '#b5e61d', gradientAngle: 120, onGradient: '#14201f',
            fontDisplay: 'georgia', fontText: 'system', ...shape
        } },
    { id: 'beach', builtIn: true, name: 'Platja', tokens: {
            mode: 'light', bg: '#fbf8f2', surface: '#ffffff', text: '#11303b', textMuted: '#46616b', line: '#e4e0d6',
            accent: '#0e6c8c', onAccent: '#ffffff', gradientFrom: '#ffe08a', gradientTo: '#8fe3d6', gradientAngle: 110, onGradient: '#11303b',
            fontDisplay: 'playfair', fontText: 'inter', radiusControl: 12, radiusCard: 16, radiusSheet: 20
        } },
    { id: 'city', builtIn: true, name: 'Ciutat', tokens: {
            mode: 'dark', bg: '#121417', surface: '#1c1f24', text: '#f2f1ee', textMuted: '#b4b6ba', line: '#30343b',
            accent: '#d9b56c', onAccent: '#121417', gradientFrom: '#e9c46a', gradientTo: '#f4a261', gradientAngle: 120, onGradient: '#121417',
            fontDisplay: 'dmSerif', fontText: 'inter', radiusControl: 4, radiusCard: 6, radiusSheet: 10
        } },
    { id: 'mountain', builtIn: true, name: 'Muntanya', tokens: {
            mode: 'light', bg: '#f6f5f0', surface: '#ffffff', text: '#1f2a22', textMuted: '#4f5b52', line: '#dcdcd2',
            accent: '#3d5a40', onAccent: '#ffffff', gradientFrom: '#c9e265', gradientTo: '#e8d27a', gradientAngle: 120, onGradient: '#1f2a22',
            fontDisplay: 'cormorant', fontText: 'lora', ...shape
        } },
    { id: 'countryside', builtIn: true, name: 'Camp', tokens: {
            mode: 'light', bg: '#faf6ee', surface: '#fffdf8', text: '#2b2118', textMuted: '#5d5045', line: '#e6dccb',
            accent: '#8a4b2a', onAccent: '#ffffff', gradientFrom: '#f6c453', gradientTo: '#f3a875', gradientAngle: 120, onGradient: '#2b2118',
            fontDisplay: 'fraunces', fontText: 'josefin', radiusControl: 10, radiusCard: 14, radiusSheet: 18
        } },
];
export const DEFAULT_SKIN_ID = 'base';
export const builtInSkin = (id) => BUILT_IN_SKINS.find((s) => s.id === id);
/** Id d'una skin nova creada a l'editor: com els de Firestore (20 caràcters), sense xocar amb el joc inicial. */
export const isBuiltInSkinId = (id) => BUILT_IN_SKINS.some((s) => s.id === id);
